import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';

console.log('🚀 Starting GCI-ChMS application...');

window.__GCI_VITE_ENV__ = {
  DEV: import.meta.env.DEV,
  PROD: import.meta.env.PROD,
  MODE: import.meta.env.MODE,
  VITE_ENABLE_GATEWAY_ADMIN: import.meta.env.VITE_ENABLE_GATEWAY_ADMIN,
  VITE_WS_URL: import.meta.env.VITE_WS_URL,
};

// Initialize the application
const root = document.getElementById('root');
if (!root) {
  console.error('❌ Root element not found!');
  throw new Error('Root element not found');
}

console.log('✅ Root element found, creating React root...');

const reactRoot = ReactDOM.createRoot(root);

console.log('✅ React root created, rendering app...');

const requiredClientEnvVars = ['VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY'] as const;
const missingRequiredEnvVars = requiredClientEnvVars.filter((key) => !import.meta.env[key]);

const MissingEnvScreen = ({ missingKeys }: { missingKeys: readonly string[] }) => (
  <div className="min-h-screen flex items-center justify-center bg-neutral-cream dark:bg-neutral-dark-700 px-4">
    <div className="w-full max-w-2xl rounded-md border border-red-200 dark:border-red-800 bg-white dark:bg-neutral-dark-800 p-6 shadow-sm">
      <h1 className="text-xl font-semibold text-red-700 dark:text-red-400">Dashboard Configuration Error</h1>
      <p className="mt-2 text-sm text-neutral-blueGray dark:text-neutral-light-500">
        Required frontend environment variables are missing. Add these in Vercel Project Settings for the deployed dashboard and redeploy.
      </p>
      <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-neutral-blueGray dark:text-neutral-light-500">
        {missingKeys.map((key) => (
          <li key={key}>{key}</li>
        ))}
      </ul>
    </div>
  </div>
);

const bootstrap = async () => {
  if (import.meta.env.DEV && 'serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
      if ('caches' in window) {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
      }
      console.log('🧹 Cleared stale service workers and caches for dashboard dev mode');
    } catch (error) {
      console.warn('Failed to clear dashboard service workers in dev mode:', error);
    }
  }

  if (missingRequiredEnvVars.length > 0) {
    console.error('❌ Missing required Vite env vars:', missingRequiredEnvVars.join(', '));
    reactRoot.render(
      <React.StrictMode>
        <MissingEnvScreen missingKeys={missingRequiredEnvVars} />
      </React.StrictMode>
    );
    return;
  }

  const { default: App } = await import('./App');

  reactRoot.render(
    <React.StrictMode>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </React.StrictMode>
  );

  console.log('✅ GCI-ChMS Application rendered successfully');
};

void bootstrap();
